import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
  FormGroup,
} from "reactstrap";
import { post, put, get } from "helpers/api_helper";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import * as api from "../../../constants/api_constants";
import CustomPagination from "components/CustomPagination";
import SummaryCards from "components/SummaryCards";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import { formattedDate } from "helpers/utils/dateUtils";
import { formatToPascal } from "helpers/utils/nameUtils";

function Employees() {
  const navigate = useNavigate();

  const [employeesData, setEmployeesData] = useState({
    employees: [],
    error: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [apiStatus, setApiStatus] = useState({ loading: false });
  const [employeesCount, setEmployeesCount] = useState({
    activeCount: 0,
    inactiveCount: 0,
    totalCount: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    sortBy: "employeeId",
    direction: "ASC",
  });
  const timerRef = useRef(null);

  const fetchEmployeesData = useCallback(
    async (query, page = 1) => {
      try {
        setApiStatus((prev) => ({ ...prev, loading: true }));

        const data = await post(api.GET_ALL_EMPLOYEES, {
          searchKeyword: query?.trim() ?? "",
          pageSize: 10,
          page: page - 1,
          direction: sortConfig.direction,
          sortBy: sortConfig.sortBy,
        });

        const empCountData = await get(api.GET_EMPLOYEE_COUNT);
        if (empCountData) {
          setEmployeesCount((prev) => ({ ...prev, ...empCountData }));
        }

        if (data && data.data.items) {
          setEmployeesData((prev) => ({
            ...prev,
            employees: data.data.items,
            error: "",
          }));
          setTotalPages(data.data?.totalPages || 0);
        }
      } catch (error) {
        setEmployeesData((prev) => ({
          ...prev,
          employees: [],
          error: error.message ?? "Error fetching data",
        }));
      } finally {
        setApiStatus((prev) => ({ ...prev, loading: false }));
      }
    },
    [sortConfig]
  );

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      fetchEmployeesData(searchQuery, page);
    },
    [fetchEmployeesData, searchQuery]
  );

  const handleSort = useCallback((columnName) => {
    setSortConfig((prevConfig) => {
      // If sorting the same column, toggle direction
      if (prevConfig.sortBy === columnName) {
        return {
          sortBy: columnName,
          direction: prevConfig.direction === "ASC" ? "DESC" : "ASC",
        };
      }
      // If sorting a new column, default to ASC
      return {
        sortBy: columnName,
        direction: "ASC",
      };
    });
  }, []);

  const SortArrows = ({ column, currentSort }) => {
    const isCurrentColumn = currentSort.sortBy === column;
    return (
      <span className="sort-arrows">
        <i
          className={`fas fa-sort-up ${
            isCurrentColumn && currentSort.direction === "ASC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
        <i
          className={`fas fa-sort-down ${
            isCurrentColumn && currentSort.direction === "DESC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
      </span>
    );
  };

  // Mapping of table columns to API sort keys
  const columnSortMap = {
    "Employee Id": "employeeId",
    "Employee Name": "firstName",
    Email: "email",
    Designation: "designation",
    "Hire Date": "hireDate",
    Status: "status",
  };

  const renderTableHeader = () => (
    <thead>
      <tr>
        {[
          "Employee Id",
          "Employee Name",
          "Email",
          "Designation",
          "Hire Date",
          "Status",
          "Actions",
        ].map((header) =>
          header !== "Actions" ? (
            <th
              key={header}
              onClick={() => handleSort(columnSortMap[header])}
              style={{ cursor: "pointer" }}
            >
              {header}
              <SortArrows
                column={columnSortMap[header]}
                currentSort={sortConfig}
              />
            </th>
          ) : (
            <th key={header}>{header}</th>
          )
        )}
      </tr>
    </thead>
  );

  const toggleEmployeeStatus = useCallback(
    async (id) => {
      try {
        // Fetch the employee data from the API
        const employeeData = await get(api.GET_EMPLOYEE_BY_ID(id));

        if (employeeData && employeeData.data) {
          const updatedEmployee = {
            ...employeeData.data,
            status: employeeData.data.status === 1 ? 0 : 1, // Toggle status
          };
          // Update the employee status with the new value
          await put(api.UPDATE_EMPLOYEE_BY_ID(id), updatedEmployee);
          showSuccessToast("Employee status updated successfully");

          // Re-fetch the employee list after status update
          await fetchEmployeesData(searchQuery, currentPage);
        }
      } catch (error) {
        console.error("Error updating employee status", error);
        showErrorToast("Failed to update employee status");
      }
    },
    [fetchEmployeesData, currentPage, searchQuery]
  );

  const handleSearch = useCallback(
    (e) => {
      const query = e.target.value || "";
      setSearchQuery(query);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        fetchEmployeesData(query?.trim());
      }, 300);
    },
    [fetchEmployeesData]
  );

  const handleEdit = useCallback(
    (empID) => {
      navigate(`/add-employee/${empID}`);
    },
    [navigate]
  );

  const summaryData = useMemo(
    () => [
      {
        iconClass: "fas fa-users text-primary",
        label: "Total Count",
        value: employeesCount.totalCount,
      },
      {
        iconClass: "fas fa-user-check text-primary",
        label: "Active Count",
        value: employeesCount.activeCount,
      },
      {
        iconClass: "fas fa-user-times text-danger",
        label: "Inactive Count",
        value: employeesCount.inactiveCount,
      },
    ],
    [employeesCount]
  );

  useEffect(() => {
    fetchEmployeesData(searchQuery, currentPage);
  }, [fetchEmployeesData, sortConfig, searchQuery, currentPage]);

  return (
    <div className="page-content">
      <Container fluid={true} className="py-0 px-0">
        <PageSubHeader
          heading={"Employees"}
          routePath={"Home"}
          pageName={"Employees"}
        />
        <Row className="px-4">
          <SummaryCards data={summaryData} />

          <Row>
            <Col>
              <Row className="d-flex justify-content-between align-items-center py-2">
                <Col xs={3}>
                  <InputGroup size="sm" className="elbert-search-group">
                    <Input
                      placeholder="Search, ID, Name, Email"
                      className="elbert-search"
                      onChange={handleSearch}
                      value={searchQuery}
                    />
                    <InputGroupText>
                      <span className="fa fa-search"></span>
                    </InputGroupText>
                  </InputGroup>
                </Col>
                <Col xs={3}></Col>
                <Col
                  xs={2}
                  className="d-flex align-items-center justify-content-end"
                >
                  <CustomButton
                    text={"+ Add New"}
                    onClick={() => navigate("/add-employee")}
                  />
                </Col>
              </Row>

              <Row>
                <Card>
                  <CardBody>
                    <div className="table-responsive">
                      <Table className="table mb-0">
                        <colgroup>
                          <col style={{ width: "12%" }} /> {/* Employee Id */}
                          <col style={{ width: "20%" }} /> {/* Employee Name */}
                          <col style={{ width: "22%" }} /> {/* Email */}
                          <col style={{ width: "15%" }} /> {/* Designation */}
                          <col style={{ width: "13%" }} /> {/* Hire Date */}
                          <col style={{ width: "8%" }} /> {/* Status */}
                          <col style={{ width: "10%" }} /> {/* Actions */}
                        </colgroup>
                        {renderTableHeader()}
                        <tbody>
                          {employeesData.employees.map((employee, index) => (
                            <tr key={index}>
                              <td>{employee.employeeCode || "N/A"}</td>
                              <td>
                                {formatToPascal(
                                  `${employee.firstName} ${employee.middleName || ''} ${employee.lastName}`
                                )}
                              </td>
                              <td>{employee.email}</td>
                              <td>{formatToPascal(employee.designation)}</td>
                              <td>{formattedDate(employee.hireDate)}</td>
                              <td>
                                <div>
                                  <FormGroup switch>
                                    <Input
                                      style={{
                                        color: "blue",
                                        height: "1.4em",
                                        width: "3em",
                                        cursor: "pointer",
                                      }}
                                      type="switch"
                                      disabled={apiStatus.loading}
                                      checked={employee.status === 1}
                                      onChange={() =>
                                        toggleEmployeeStatus(employee.employeeId)
                                      }
                                    />
                                  </FormGroup>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex gap-3 align-items-center">
                                  <i
                                    className="fa-solid fa-eye action-icon"
                                    title="View"
                                    onClick={() =>
                                      navigate(`/view-employee/${employee.employeeId}`)
                                    }
                                  ></i>
                                  <i
                                    onClick={() => handleEdit(employee.employeeId)}
                                    className="fa-regular fa-pen-to-square action-icon"
                                    title="Edit"
                                  ></i>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {employeesData.error && (
                      <div>
                        <p className="text-danger text-center m-3">
                          {employeesData.error}
                        </p>
                      </div>
                    )}
                    {!apiStatus.loading && !employeesData.employees.length && (
                      <p className="text-center m-3">No Employees data found</p>
                    )}
                  </CardBody>
                </Card>
              </Row>
            </Col>
          </Row>

          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Row>
      </Container>
    </div>
  );
}

export default Employees;